/**
 * Ce qu'un écran affiche quand une **lecture** a échoué
 * (`ARCHITECTURE_V2.md` §3.5, étape 2 de la V2).
 *
 * 🔴 **Deux natures d'échec, deux tons — et les confondre était le défaut
 * trouvé à l'usage.** L'étape 1 avait déjà séparé `ApiError` de `NetworkError` à
 * la source ; les écrans, eux, les affichaient encore de la même façon : un
 * bandeau rouge. Hors ligne, l'application couverte de rouge annonçait une panne
 * là où elle fonctionnait exactement comme prévu.
 *
 * Le §3.5 est explicite : « Ne **jamais** présenter “hors ligne” comme une
 * erreur : en V2 c'est un mode nominal. » Ce composant est l'endroit unique où
 * cette règle s'applique, pour qu'elle ne se redécide pas écran par écran.
 *
 * | Situation | Rendu |
 * |-|-|
 * | Données en cache **et** échec de transport | **rien** — `readError` a déjà tranché, les données s'affichent |
 * | Aucune donnée **et** échec de transport | note **neutre** : il n'y a rien à montrer, ce n'est pas une panne |
 * | Le serveur a répondu et refuse | bandeau d'**erreur** — celui-là ne se répare pas en attendant |
 *
 * ⚠️ Dans son propre fichier plutôt que dans `primitives.tsx` : celui-ci est une
 * bibliothèque de briques visuelles sans connaissance de l'API, et lui faire
 * importer `api/client` mêlerait deux couches que le §3 tient séparées.
 */

import { isTransportFailure } from '../../api/client';
import { ErrorNote, PendingNote } from './primitives';

export function ReadNote({
  error,
  /**
   * Ce qui manque, au singulier et sans article — « cette journée », « cet
   * écran ». Sert uniquement à la formulation hors ligne : un message qui nomme
   * ce qu'il ne peut pas montrer se comprend sans avoir à deviner.
   */
  quoi = 'cet écran',
}: {
  error: Error | null;
  quoi?: string;
}) {
  if (!error) return null;

  if (isTransportFailure(error)) {
    return (
      <PendingNote>
        Pas de connexion, et {quoi} n’a rien en mémoire sur cet appareil. Le contenu
        s’affichera après un premier passage en ligne.
      </PendingNote>
    );
  }

  return <ErrorNote>{error.message}</ErrorNote>;
}
